import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Star, Users, Clock, CheckCircle, ArrowRight, Building2, MessageCircle, ChevronRight, BadgeCheck, Heart, Lock, Sparkles, Search } from 'lucide-react';
import FavoriteButton from '../components/FavoriteButton';
import { useAuth } from '../context/AuthContext';
import AuthModal from '../components/AuthModal';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import axios from 'axios';
import { getVenueUrl } from '../utils/slugHelper';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const optimizeImageUrl = (url, width = 500) => {
  if (!url || typeof url !== 'string') return '';
  if (url.includes('res.cloudinary.com') && url.includes('/upload/')) {
    const afterUpload = url.split('/upload/')[1];
    if (afterUpload && !afterUpload.startsWith('v')) return url;
    return url.replace('/upload/', `/upload/w_${width},q_auto,f_auto,fl_progressive/`);
  }
  return url;
};

const isVenueClaimed = (venue) => {
  if (!venue) return false;
  const plan = venue.currentPlan?.toLowerCase();
  const isPro = plan === 'pro' || plan === 'premium';
  return venue.isClaimed === true || (venue.ownerId && venue.ownerId !== '') || isPro;
};

const getLocativeSuffix = (name) => {
  if (!name) return 'deki';
  const lastChar = name.slice(-1).toLowerCase();
  const turkishVowels = ['a', 'e', 'i', 'o', 'u'];
  const softConsonants = ['c', 'f', 'h', 'k', 'p', 's', 't'];
  const vowels = name.toLowerCase().split('').filter(c => turkishVowels.includes(c));
  const lastVowel = vowels[vowels.length - 1];
  const isHard = ['a', 'o', 'u'].includes(lastVowel);
  if (turkishVowels.includes(lastChar)) return isHard ? "'da" : "'de";
  if (softConsonants.includes(lastChar)) return isHard ? "'ta" : "'te";
  return isHard ? "'da" : "'de";
};

// VenueCard - VenuesPage ile ayni tasarim
const VenueCard = ({ venue, navigate, onAuthRequired, venueIndex = 0 }) => {
  const images = venue.images || [];
  const featuredIndex = venue.featuredImageIndex || 0;
  const mainImage = images[featuredIndex] || images[0] || '';
  const hasCampaign = venue.activeCampaigns && venue.activeCampaigns.length > 0;
  const isPro = venue.currentPlan?.toLowerCase() === 'pro' || venue.currentPlan?.toLowerCase() === 'premium';

  const getMinPrice = () => {
    if (!venue.packages || venue.packages.length === 0) return null;
    const prices = venue.packages.filter(p => p.price > 0).map(p => p.price);
    return prices.length ? Math.min(...prices) : null;
  };
  const minPrice = getMinPrice();

  const campaignDiscount = hasCampaign ? venue.activeCampaigns[0] : null;

  const renderStars = (rating) => {
    const full = Math.floor(rating);
    const half = rating % 1 >= 0.5;
    return Array.from({ length: 5 }, (_, i) => (
      <Star key={i} className={`w-4 h-4 ${i < full ? 'fill-amber-400 text-amber-400' : i === full && half ? 'fill-amber-400/50 text-amber-400' : 'text-gray-300'}`} />
    ));
  };

  const optimizedImage = optimizeImageUrl(mainImage, 500);

  return (
    <div className={`relative ${hasCampaign ? 'p-[3px] rounded-[26px] bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 animate-gradient-x' : ''}`}>
      <div className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer group"
        onClick={() => navigate(getVenueUrl(venue))}>

        {/* Image */}
        <div className="relative aspect-[4/3] overflow-hidden bg-gray-100">
          {optimizedImage ? (
            <img src={optimizedImage} alt={venue.name}
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              loading={venueIndex < 4 ? "eager" : "lazy"}
              fetchPriority={venueIndex < 2 ? "high" : "auto"} />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-pink-50 via-purple-50 to-rose-50 flex items-center justify-center">
              <Building2 className="w-16 h-16 text-pink-200" />
            </div>
          )}

          {/* Campaign Badge */}
          {venue.isClaimed && hasCampaign && campaignDiscount && (
            <div className="absolute top-3 left-3 z-10 bg-gradient-to-r from-red-500 to-pink-600 text-white px-3 py-1.5 rounded-full text-sm font-bold shadow-lg animate-pulse">
              {campaignDiscount.discountType === 'percentage'
                ? `%${campaignDiscount.discountValue} Indirim!`
                : `${campaignDiscount.discountValue?.toLocaleString('tr-TR')} TL Indirim!`}
            </div>
          )}

          {/* Price Badge */}
          {venue.isClaimed && !hasCampaign && minPrice && (
            <div className="absolute top-3 left-3 z-10 bg-gradient-to-r from-green-500 to-emerald-600 text-white px-3 py-1.5 rounded-full text-sm font-bold shadow-lg">
              {minPrice.toLocaleString('tr-TR')} TL'den baslayan
            </div>
          )}

          {/* Randevu Badge */}
          {venue.visitSettings?.enabled && (
            <div className="absolute bottom-3 right-3 z-10 bg-purple-600/90 backdrop-blur-sm text-white px-2 py-1 rounded-lg text-[10px] font-semibold shadow-md flex items-center gap-1">
              <Clock className="w-2.5 h-2.5" /> Randevu
            </div>
          )}

          {/* Favorite Button */}
          <div className="absolute top-3 right-3 z-10">
            <FavoriteButton venueId={venue._id || venue.id} venueName={venue.name} venueSlug={venue.slug} onAuthRequired={onAuthRequired} size="small" />
          </div>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-5">
          {/* Name + Badge */}
          <div className="flex items-center gap-2 mb-2">
            <h3 className="font-bold text-lg text-gray-900 truncate">{venue.name}</h3>
            {isPro && <BadgeCheck className="w-5 h-5 text-blue-500 fill-blue-100 flex-shrink-0" />}
          </div>

          {/* Rating */}
          {(venue.googleRating || venue.rating) > 0 && (
            <div className="flex items-center gap-2 mb-2">
              <span className="font-semibold text-gray-900">{(venue.googleRating || venue.rating).toFixed(1)}</span>
              <div className="flex items-center gap-0.5">{renderStars(venue.googleRating || venue.rating)}</div>
              {(venue.googleReviewCount || venue.reviewCount) > 0 && (
                <span className="text-gray-500 text-sm">({venue.googleReviewCount || venue.reviewCount})</span>
              )}
            </div>
          )}

          {/* Location + Capacity */}
          <div className="flex items-center gap-3 text-gray-500 mb-3">
            <div className="flex items-center gap-1.5">
              <MapPin className="w-4 h-4 text-pink-500 flex-shrink-0" />
              <span className="text-sm truncate">{venue.districtName}</span>
            </div>
            {venue.capacity?.min && venue.capacity?.max && (
              <div className="flex items-center gap-1">
                <Users className="w-4 h-4 text-pink-400" />
                <span className="text-sm">{venue.capacity.min}-{venue.capacity.max}</span>
              </div>
            )}
          </div>

          {/* Campaign Name */}
          {hasCampaign && campaignDiscount && (
            <div className="mb-3 text-xs font-medium text-pink-600 bg-pink-50 px-2 py-1 rounded-lg truncate">
              {campaignDiscount.campaignName}
            </div>
          )}

          {/* Buttons */}
          <div className="flex flex-col gap-2">
            {venue.smartPricingEnabled && isPro && (
              <button className="w-full py-2.5 rounded-xl font-semibold text-sm transition-all duration-200 flex items-center justify-center gap-2 bg-gradient-to-r from-rose-500 via-pink-500 to-purple-600 text-white hover:from-rose-600 hover:via-pink-600 hover:to-purple-700 shadow-lg hover:shadow-xl hover:-translate-y-0.5"
                data-testid="smart-package-btn"
                onClick={(e) => { e.stopPropagation(); navigate(getVenueUrl(venue) + '#smart-package-builder'); }}>
                <Sparkles className="w-4 h-4" /> Hemen Paketini Olustur
              </button>
            )}
            {!venue.smartPricingEnabled && isPro && (
              <button className="w-full py-2.5 rounded-xl font-semibold text-sm transition-all duration-200 flex items-center justify-center gap-2 bg-gradient-to-r from-emerald-500 to-teal-600 text-white hover:from-emerald-600 hover:to-teal-700 shadow-md hover:shadow-lg"
                onClick={(e) => { e.stopPropagation(); navigate(getVenueUrl(venue)); }}>
                <MessageCircle className="w-4 h-4" /> Teklif Al
              </button>
            )}
            <button className={`w-full py-2.5 rounded-xl font-semibold text-sm transition-all duration-200 flex items-center justify-center gap-1 ${hasCampaign ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white hover:from-pink-600 hover:to-purple-700' : 'border-2 border-gray-200 text-pink-600 hover:border-pink-300 hover:bg-pink-50'}`}
              onClick={(e) => { e.stopPropagation(); navigate(getVenueUrl(venue)); }}>
              {hasCampaign ? 'Kampanyayi Gor' : 'Detaylari Gor'} <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const DistrictVenuePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { city: citySlug, district: districtSlug } = useParams();
  const { isAuthenticated } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formSuccess, setFormSuccess] = useState(false);
  const [consultationFormData, setConsultationFormData] = useState({ name: '', phone: '', appointment_date: '', appointment_time: '' });

  useEffect(() => { loadPageData(); }, [citySlug, districtSlug]);

  const loadPageData = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${BACKEND_URL}/api/district-page/${citySlug}/${districtSlug}`);
      setPageData(res.data);
    } catch (err) { console.error('Error loading district page:', err); }
    finally { setLoading(false); }
  };

  const handleConsultationFormChange = (e) => {
    setConsultationFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleConsultationSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/district-consultation-request`, {
        ...consultationFormData, citySlug, districtSlug,
        cityName: pageData?.city?.name, districtName: pageData?.district?.name
      });
      setFormSuccess(true);
      setConsultationFormData({ name: '', phone: '', appointment_date: '', appointment_time: '' });
    } catch (err) { console.error('Consultation error:', err); }
    finally { setFormSubmitting(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-10 h-10 border-4 border-pink-200 border-t-pink-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (!pageData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-700 mb-2">Sayfa Bulunamadi</h2>
          <Button onClick={() => navigate('/')} variant="outline">Ana Sayfaya Don</Button>
        </div>
      </div>
    );
  }

  const { city, district, venues = [], venue_count, has_venues, nearby_districts = [], faq = [] } = pageData;
  const smartPricingCount = venues.filter(v => v.smartPricingEnabled).length;

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>{city.name} {district.name} Davet Evleri | DavetEviBul.com</title>
        <meta name="description" content={`${city.name} ${district.name} davet evi fiyatlari, kampanyalar ve rezervasyon. ${venue_count} davet evi listeleniyor.`} />
        <meta name="keywords" content={`${district.name} davet evi, ${city.name} ${district.name} nişan salonu, ${district.name} isteme salonu, ${district.name} kına gecesi`} />
        <link rel="canonical" href={`https://davetevibul.com/${citySlug}/${districtSlug}`} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://davetevibul.com/${citySlug}/${districtSlug}`} />
        <meta property="og:title" content={`${city.name} ${district.name} Davet Evleri | DavetEviBul.com`} />
        <meta property="og:description" content={`${city.name} ${district.name} bölgesinde ${venue_count} davet evi. Fiyat karşılaştırma ve online teklif alın.`} />
        <meta property="og:locale" content="tr_TR" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={`${city.name} ${district.name} Davet Evleri`} />
        {faq && faq.length > 0 && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org", "@type": "FAQPage",
              "mainEntity": faq.map(item => ({ "@type": "Question", "name": item.question, "acceptedAnswer": { "@type": "Answer", "text": item.answer } }))
            })}
          </script>
        )}
      </Helmet>

      {/* Compact Header */}
      <div className="bg-white border-b border-gray-100 sticky top-0 z-30">
        <div className="container mx-auto px-4 py-3">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
            <Link to="/" className="hover:text-pink-600 transition-colors">Ana Sayfa</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <Link to={`/${citySlug}`} className="hover:text-pink-600 transition-colors">{city.name}</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <span className="text-gray-900 font-medium">{district.name}</span>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
                {district.name} Davet Evleri
              </h1>
              <Badge className="bg-pink-100 text-pink-700 border-pink-200 text-xs font-semibold">{venue_count} mekan</Badge>
            </div>
          </div>
        </div>
      </div>

      {/* Success Message */}
      {formSuccess && (
        <div className="container mx-auto px-4 mt-4">
          <div className="bg-green-50 border border-green-200 rounded-2xl p-4 flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
            <div>
              <p className="font-semibold text-green-800">Talebiniz Alindi!</p>
              <p className="text-green-700 text-sm">2 saat icinde size en uygun tekliflerle donus yapacagiz.</p>
            </div>
          </div>
        </div>
      )}

      <div className="container mx-auto px-4 py-6">
        <div className="max-w-7xl mx-auto">

          {/* Venue Cards Grid */}
          {has_venues && venues.length > 0 && (
            <div className="mb-10">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {venues.map((venue, idx) => (
                  <VenueCard key={venue._id || venue.id} venue={venue} navigate={navigate}
                    onAuthRequired={() => setShowAuthModal(true)} venueIndex={idx} />
                ))}
              </div>
            </div>
          )}

          {/* Auth Modal */}
          <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />

          {/* Consultation Form - Only when NO venues */}
          {!has_venues && (
            <Card className="mb-10 border-0 shadow-xl rounded-3xl overflow-hidden">
              <CardContent className="p-0">
                <div className="bg-gradient-to-br from-teal-500 to-cyan-600 p-6 text-white text-center">
                  <h2 className="text-2xl font-bold mb-2">Davet Evi Danismanlarimizdan Randevunu Al</h2>
                  <p className="text-white/80 text-sm">
                    {district.name}{getLocativeSuffix(district.name)}ki sana uygun davet evini bul
                  </p>
                  <div className="flex flex-wrap justify-center gap-3 mt-4 text-xs">
                    <span className="flex items-center gap-1 bg-white/20 px-3 py-1.5 rounded-full"><CheckCircle className="w-3.5 h-3.5" /> Ucretsiz</span>
                    <span className="flex items-center gap-1 bg-white/20 px-3 py-1.5 rounded-full"><Clock className="w-3.5 h-3.5" /> 2 Saat Donus</span>
                    <span className="flex items-center gap-1 bg-white/20 px-3 py-1.5 rounded-full"><CheckCircle className="w-3.5 h-3.5" /> En Az 3 Teklif</span>
                  </div>
                </div>
                <form onSubmit={handleConsultationSubmit} className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Telefon</label>
                      <div className="flex">
                        <span className="flex items-center px-3 bg-gray-100 border border-r-0 rounded-l-xl text-sm text-gray-600">+90</span>
                        <input type="tel" name="phone" placeholder="5XX XXX XX XX" required value={consultationFormData.phone}
                          onChange={handleConsultationFormChange} className="flex-1 px-3 py-2.5 border rounded-r-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Ad Soyad</label>
                      <input type="text" name="name" placeholder="Adiniz Soyadiniz" required value={consultationFormData.name}
                        onChange={handleConsultationFormChange} className="w-full px-3 py-2.5 border rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Tarih</label>
                      <input type="date" name="appointment_date" required min={new Date().toISOString().split('T')[0]}
                        value={consultationFormData.appointment_date} onChange={handleConsultationFormChange}
                        className="w-full px-3 py-2.5 border rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Saat</label>
                      <select name="appointment_time" required value={consultationFormData.appointment_time}
                        onChange={handleConsultationFormChange}
                        className="w-full px-3 py-2.5 border rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm bg-white">
                        <option value="">Saat Secin</option>
                        {['11:00-12:00','12:00-13:00','13:00-14:00','14:00-15:00','15:00-16:00','16:00-17:00','17:00-18:00','18:00-19:00','19:00-20:00','20:00-21:00','21:00-22:00'].map(t => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <Button type="submit" disabled={formSubmitting}
                    className="w-full mt-5 bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-white py-3 text-base font-semibold rounded-xl shadow-lg">
                    {formSubmitting ? 'Gonderiliyor...' : 'Seni Arayalim'}
                  </Button>
                  <p className="text-center text-xs text-gray-400 mt-3">Bilgileriniz guvende | Ucretsiz danismanlik | En uygun fiyat garantisi</p>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Nearby Districts */}
          {nearby_districts && nearby_districts.length > 0 && (
            <div className="mb-10">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Yakin Ilcelerde Davet Evleri</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {nearby_districts.map((nd) => (
                  <div key={nd.slug} onClick={() => navigate(`/${citySlug}/${nd.slug}`)}
                    className="group bg-white rounded-2xl p-5 border border-gray-100 hover:border-pink-200 shadow-sm hover:shadow-lg transition-all duration-300 cursor-pointer">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-bold text-gray-900 group-hover:text-pink-600 transition-colors">{nd.name}</h3>
                        <p className="text-pink-600 font-semibold text-sm mt-1">{nd.venue_count} davet evi</p>
                      </div>
                      <ArrowRight className="w-5 h-5 text-gray-300 group-hover:text-pink-500 group-hover:translate-x-1 transition-all" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* FAQ */}
          {!has_venues && faq.length > 0 && (
            <div className="mb-10">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Sikca Sorulan Sorular</h2>
              <div className="space-y-3">
                {faq.map((item, i) => (
                  <div key={i} className="bg-white rounded-2xl p-5 border border-gray-100">
                    <h3 className="font-semibold text-gray-900 mb-2">{item.question}</h3>
                    <p className="text-gray-600 text-sm">{item.answer}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DistrictVenuePage;
